import { Button } from '@mui/material';
import isEqual from 'lodash/isEqual';
import * as React from 'react';

import { SendItemsForm } from '../../../components';
import { IChangeBucketItemPayload } from '../../../types/bucket';
import { ICampaignItem } from '../../../types/campaigns';
import { IOnDemandItem } from '../../../types/inventories';
import { EngagementSourceTypesEnum, IReportEngagementItem, IReportWithCampaignSummaries } from '../../../types/reports';
import { BoxDetailsSidebarModeEnum } from '../../../types/shell';
import { isValidScheduledEngagement } from '../../../utils/reports';
import AddSendItemsSidebar from '../../AddSendItemsSidebar/AddSendItemsSidebar';
import { actions, reducer } from './reducer';

import styles from './EditEngagementItemsContainer.module.scss';

const buttonStyles = { fontSize: '15px', lineHeight: 1.7, py: 1, px: 2.75 };

export interface IEditItemsRef {
  items?: IReportEngagementItem[] | null;
  getValues: () => IReportEngagementItem[];
}

interface IProps {
  onSubmit: (items: IReportEngagementItem[]) => void;
  report: IReportWithCampaignSummaries | null | undefined;
}

const EditEngagementItemsContainer = React.forwardRef(({ report, onSubmit }: IProps, ref: React.Ref<IEditItemsRef>) => {
  const [isAddItemsSidebarShown, setIsAddItemsSidebarShown] = React.useState<boolean>(false);
  const [items, changeItems] = React.useReducer(reducer(report?.items), report?.items || []);

  const handleItemChange = React.useCallback((payload: IChangeBucketItemPayload) => {
    changeItems(actions.change(payload));
  }, []);

  const handleRemove = React.useCallback(({ item_id }: ICampaignItem | IOnDemandItem) => {
    changeItems(actions.remove(item_id));
  }, []);

  const handleAddItemsSubmit = React.useCallback((itemCandidates?: IReportEngagementItem[]) => {
    changeItems(actions.replace(itemCandidates || []));
    setIsAddItemsSidebarShown(false);
  }, []);

  const actionButtons = React.useMemo(
    () => [
      <Button
        variant="contained"
        size="small"
        key="submit-button"
        onClick={() => onSubmit(items)}
        disabled={isEqual(items, report?.items)}
        sx={buttonStyles}
        data-action="save_changes_btn"
      >
        Save changes
      </Button>,
    ],
    [items, report?.items, onSubmit],
  );

  const isEditingSendItemsAllowed = React.useMemo(() => {
    return report && !isValidScheduledEngagement(report);
  }, [report]);

  const isDirectSendCampaign = React.useMemo(() => {
    return report?.engagement_source_type === EngagementSourceTypesEnum.DirectSend;
  }, [report]);

  const isDirectSend = React.useMemo(() => {
    return report?.engagement_source_type === EngagementSourceTypesEnum.DirectSend;
  }, [report]);

  React.useImperativeHandle(
    ref,
    () => ({
      getValues: () => items,
    }),
    [items],
  );

  return (
    <React.Fragment>
      <div className={styles.headingContainer}>
        <div className={styles.title}>Edit Items</div>
        {isEditingSendItemsAllowed && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setIsAddItemsSidebarShown(true)}
            sx={buttonStyles}
            data-action="edit_items_btn"
          >
            Edit items
          </Button>
        )}
      </div>
      <div className={styles.itemsContainer}>
        <SendItemsForm
          mode={BoxDetailsSidebarModeEnum.Edit}
          onChange={handleItemChange}
          onRemove={handleRemove}
          items={items}
          canRemoveAll={false}
          isDirectSend={isDirectSendCampaign}
        />
      </div>
      <div className={styles.footerContainer}>{actionButtons}</div>
      {isEditingSendItemsAllowed && (
        <AddSendItemsSidebar
          onClose={() => setIsAddItemsSidebarShown(false)}
          isShown={isAddItemsSidebarShown}
          onSubmit={handleAddItemsSubmit}
          reportItems={items}
          status={report?.status}
          isDirectSend={isDirectSend}
        />
      )}
    </React.Fragment>
  );
});

export default EditEngagementItemsContainer;
